import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import IconButton from "components/common/iconButton";
import { Link } from "react-router-dom";
import { Card, Col, Row, Modal, Spinner, Button } from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import ManualPaginationFooter from "components/common/advance-table/manualPaginationFooter";
import useServerAdvanceTable from "hooks/useServerAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import paths from "routes/paths";
import axios from "axios"; // Import Axios
import FalconCloseButton from "components/common/falconCloseButton";
import { toast } from "react-toastify";
import { formatDate } from "assets/js/common";
import { useAppContext } from "../../providers/appProvider";
import { useTranslation } from "react-i18next";
import {
  fetchRoleAndClaims,
  handleLoadingAndAccess,
  getInitialPageSize,
  getProposalStatusBadge,
  getTranslatedSalutation 
} from "../../assets/js/common";
import moment from "moment";

const deleteOffer = async (proposalId) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/proposalRoute/deleteProposalById/${proposalId}`,{
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
    );
    if (response.status === 200) {
      toast.success("Offertes is verwijderd", {
        position: "top-right",
        autoClose: 5000,
      });
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  } catch (error) {
    toast.error(
      "Er is iets misgegaan, vul alstublieft het formulier opnieuw in",
      {
        position: "top-right",
        autoClose: 5000,
      }
    );
    console.error(
      "Error deleting relation:",
      error.response ? error.response.data : error.message
    );
  }
};

// Modal component for deletion confirmation
const StaticBackdropModal = ({ show, handleClose, proposalId }) => {
  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header>
        <Modal.Title>Bevestiging</Modal.Title>
        <FalconCloseButton onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>Weet u zeker dat u deze offerte wilt verwijderen?</Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Annuleren
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            deleteOffer(proposalId);
            handleClose();
          }}
        >
          Verwijderen
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const Proposals = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedproposalId, setSelectedproposalId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { config } = useAppContext();
  const { t } = useTranslation();
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(getInitialPageSize("proposalsTable"));
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const handleClose = () => setShowModal(false);
  const handleShow = (proposalId) => {
    setSelectedproposalId(proposalId);
    setShowModal(true);
  };

  const handleViewDetails = (proposalId) => {
    navigate(`${paths.proposalDetails.replace(":proposalId", proposalId.toLowerCase())}`);
  };

  const handleEditproposalDetails = (proposalId) => {
    localStorage.setItem("proposalId", proposalId.toLowerCase());
    navigate(paths.editProposal); // Correctly pass relationId as part of an object in state
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "proposal",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPageIndex(0);
  }, [config.location, debouncedSearch]);

  useEffect(() => {
    const fetchProposals = async () => {
      setPageLoading(true);
      try {
        const locationId = localStorage.getItem("LocationId");
        const customerId = localStorage.getItem("customerId");

        if (!locationId || !customerId) {
          console.error("LocationId and CustomerId are missing");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalsPaginated`,
          {
            params: {
              locationId,
              customerId,
              page: pageIndex + 1,
              pageSize,
              search: debouncedSearch,
            },
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
        );

        setProposals(response.data.proposals || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalCount(response.data.totalCount || 0);
      } catch (error) {
        console.error("Error fetching proposal data:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchProposals();
  }, [config.location, pageIndex, pageSize, debouncedSearch]);

  // Define columns for your Proposals table
  const columns = [
    {
      accessorKey: "ProposalNumber",
      header: t("proposalNumber"),
      cell: ({ row: { original } }) => <span>{original.ProposalNumber}</span>,
    },
    {
      accessorKey: "ClientName",
      header: t("clientName"),
      meta: {
        headerProps: { className: "pe-1 text-900" },
      },
      cell: ({ row: { original } }) => (
        <span>{original.ClientName || "--"}</span>
      ),
    },
    {
      accessorKey: "Location",
      header: t("branch"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => <span>{original.Location}</span>,
    },
    {
      accessorKey: "Date",
      header: t("requestDate"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "" },
      },
      cell: ({ row: { original } }) => {
        const { Date } = original;
        return <span>{Date ? moment(Date).format("DD-MM-YYYY") : "--"}</span>;
      },
    },
    {
      accessorKey: "Status",
      header: t("status"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
      cell: ({ row: { original } }) => getProposalStatusBadge(original.Status, t, true),
    },
    {
      accessorKey: "Contactperson",
      header: t("contactPerson"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => (
        <span>{getTranslatedSalutation(original, t) || "--"}</span>
      ),
    },
    {
      accessorKey: "TemplateName",
      header: t("workType"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => {
        return <span>{original.TemplateName}</span>;
      },
    },
    {
      accessorKey: "DueDate",
      header: t("expirationDate"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "" }, // Remove the 'fs-9' class
      },
      cell: ({ row: { original } }) => {
        const { DueDate } = original;
        return <span>{DueDate ? formatDate(DueDate) : "--"}</span>; // Display formatted date
      },
    },
    {
      accessorKey: "EmployeeName",
      header: t("createdByWithoutcolon"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => (
        <span>{original.EmployeeName || "--"}</span>
      ),
    },
  ];

  const table = useServerAdvanceTable({
    data: proposals,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    manualPagination: true,
    pageCount: totalPages,
    paginationState: { pageIndex, pageSize },
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
  });

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
    paths.dashboard
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  return (
    <>
      <AdvanceTableProvider {...table}>
        <Row className="g-3 mb-3">
          <Col lg={12} xxl={12}>
            <Card className="mb-3 h-100">
              <Background
                image={corner4}
                className="bg-card"
                style={{
                  borderTopRightRadius: "0.375rem",
                  borderBottomRightRadius: "0.375rem",
                }}
              />
              <Card.Body className="position-relative pe-3 pb-2">
                <Row className="flex-between-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0"> {t("proposals")}</h5>
                  </Col>
                  <Col xs="auto">
                    <IconButton
                      as={Link}
                      to={paths.newProposal}
                      variant="primary"
                      size="sm"
                      icon="plus"
                      transform="shrink-3"
                      data-tour="new-proposal-btn"
                    >
                      <span className="ms-1">{t("newProposal")}</span>
                    </IconButton>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Card className="mb-3">
          <Card.Header>
            <Row className="flex-between-center">
              <Col xs={8} sm="auto" className="ms-auto text-end ps-0">
                <AdvanceTableSearchBox setGlobalFilter={setSearchInput} />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {pageLoading && proposals.length === 0 ? (
              // Spinner while loading
              <div className="d-flex justify-content-center align-items-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">{t("loading")}</span>
                </Spinner>
              </div>
            ) : proposals.length === 0 ? (
              <div className="text-center pt-4">
                {t("noDataAvailableText")}
              </div>
            ) : (
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle text-center"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  bordered: true,
                  striped: true,
                  size: "sm",
                  className:
                    "mb-0 overflow-hidden data-font-size table-hover",
                }}
                getRowProps={(row) => ({
                  onClick: () => handleViewDetails(row.original.ProposalId),
                  style: { cursor: "pointer" },
                })}
              />
            )}
          </Card.Body>
          <Card.Footer>
            <ManualPaginationFooter
              totalCount={totalCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              tableKey="proposalsTable"
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPageIndex(0);
              }}
            />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
      <StaticBackdropModal
        show={showModal}
        handleClose={handleClose}
        proposalId={selectedproposalId}
      />
    </>
  );
};

export default Proposals;
